import { useEffect, useState, useMemo } from "react";
import { useRecoilState } from "recoil";

import { AllstarUser } from "../../State";
import { EClipsViews } from "../../@types";
import { ESupportedGames } from "MVVM/@types";
import { ProfileClipsViewModel } from "MVVM/Views/Profile/Clips/ViewModel";
import { useUserProfile } from "./hooks/useUserProfile";
import { useFollowers } from "./hooks/useFollowers";
import { useFilters } from "./hooks/useFilters";
import {
  useProfileFeed,
  IClipFeedQuery,
  IRefetchQuery,
} from "./hooks/useProfileFeed";

type TLocationState = {
  fromRemix: boolean;
  queuedMobile: boolean;
  fromEdit: boolean;
};

export const ProfileViewModel = (
  userSearch: string | undefined,
  locationState: TLocationState,
  view: EClipsViews,
  game: ESupportedGames,
  isUsernameQuery?: boolean,
) => {
  const fromRemix = locationState?.fromRemix;
  const [allstarUser] = useRecoilState(AllstarUser);
  const [searchFor, setSearchFor] = useState(userSearch);

  const {
    user,
    ready,
    error: profileError,
    fatalError,
    fetchUserProfile,
    clearProfile,
  } = useUserProfile(searchFor, isUsernameQuery);

  const { mutateFollowers } = useFollowers();

  const {
    loading: feedLoading,
    data: feedData,
    error: feedError,
    fetchData,
    refetchData,
    mutateReaction,
    deleteMontage,
  } = useProfileFeed(view);

  const {
    fetchFilters,
    filterData,
    filterLoading,
    filterError,
    formattedGame,
  } = useFilters(game);

  useEffect(() => {
    if (!user) fetchUserProfile();
  }, [searchFor, user, fetchUserProfile]);

  const clearViewModelState = (userSearch: string) => {
    clearProfile();
    setSearchFor(userSearch);
  };

  const {
    refetch: userClipsRefetch,
    error: userError,
    loading: userLoading,
    data: userData,
    pageInfo,
    handleFetchWithFilters,
    networkStatus: userNetworkStatus,
  } = ProfileClipsViewModel({
    game: formattedGame,
    user: user?.userId,
  });

  const clips = useMemo(() => ({
    mutateReaction,
    deleteMontage,
    ClipsFeed: (query: IClipFeedQuery) => fetchData(query, user?.userId),
    refetchClipsFeed: (query: IRefetchQuery) =>
      refetchData(query, user?.userId),
    loading: feedLoading,
    montagesLoading: feedLoading,
    data: feedData,
    error: feedError || profileError,
  }), [mutateReaction, deleteMontage, fetchData, refetchData, user?.userId, feedLoading, feedData, feedError, profileError, allstarUser]);

  return {
    ready,
    fatalError,
    user,
    clearViewModelState,
    allstarUser,
    mutateFollowers,
    fromRemix,
    clips,
    fetchFilters,
    filterData,
    filterLoading,
    filterError,
    userClipsRefetch,
    userLoading,
    userError,
    userData,
    userNetworkStatus,
    pageInfo,
    handleFetchWithFilters,
  };
};
